import React, { useState } from "react"
import * as styles from "./footer.module.scss"
import {
  FooterFieldsFragment,
  FooterMenuFieldsFragment,
} from "../../../../graphql-types"
import { graphql } from "gatsby"
import arrow from "../../../../static/images/arrow.png"
import facebookLogo from "../../../../static/images/facebook_logo.png"
import keolis from "../../../../static/images/keolis.png"
import twitterLogo from "../../../../static/images/twitter_logo.png"
import linkedInLogo from "../../../../static/images/linkedin_logo.png"
import youtubeLogo from "../../../../static/images/youtube_logo.png"
import FooterBarMenu from "./footer-bar-menu/footer-bar-menu"
import FooterTableMenu from "./footer-table-menu/footer-table-menu"
import useWindowWidth from "../../../hooks/use-window-width"
import { ReactSVG } from "react-svg"

const ENTER_KEY_CODE = 13
const SPACE_KEY_CODE = 32

type RenderProps = {
  data: FooterFieldsFragment
  menuData: FooterMenuFieldsFragment
}

const Footer: React.FC<RenderProps> = ({ data, menuData }) => {
  const MOBILE: number = 660
  const width = useWindowWidth()
  const [isOpen, setIsOpen] = useState(width > MOBILE)

  const handleKeypress = e => {
    const key_code = e.keyCode ? e.keyCode : e.which
    if (key_code === ENTER_KEY_CODE || key_code === SPACE_KEY_CODE) {
      setIsOpen(!isOpen)
    }
  }

  return (
    <footer className={styles.globalFooter} role="contentinfo">
      <div
        className={
          isOpen ? styles.collapsibleFooterOpen : styles.collapsibleFooter
        }
      >
        <div className={styles.collapsibleFooterLogos}>
          <img
            src={data.subsidiaryLogo ? data.subsidiaryLogo.url : keolis}
            alt={
              data.subsidiaryLogo && data.subsidiaryLogo.alt
                ? data.subsidiaryLogo.alt
                : "Keolis"
            }
            className={styles.keolisLogo}
            width={
              data.subsidiaryLogo && data.subsidiaryLogo.width /* 289 */
            }
            height={
              data.subsidiaryLogo && data.subsidiaryLogo.height /* 59 */
            }
          />
          <div className={styles.transportIcons}>
            {data.transportMethodsLink.map((transportMethod, index) => {
              return transportMethod.pictogram.format === "svg" ? (
                <ReactSVG
                  src={transportMethod.pictogram.url}
                  alt={transportMethod.pictogram.alt || ""}
                  className={styles.svgImage}
                  aria-hidden="true"
                  key={index}
                />
              ) : (
                <img
                  src={transportMethod.pictogram.url}
                  alt={transportMethod.pictogram.alt || ""}
                  className={styles.transportIcon}
                  key={index}
                />
              )
            })}
          </div>
          <div className={styles.socialNetworksIcons} role="list">
            {data.facebookUrl && (
              <span role="listitem">
                <a href={data.facebookUrl}>
                  <img
                    src={facebookLogo}
                    alt="facebook"
                    className={styles.socialNetworkIcon}
                  />
                </a>
              </span>
            )}
            {data.twitterUrl && (
              <span role="listitem">
                <a href={data.twitterUrl}>
                  <img
                    src={twitterLogo}
                    alt="twitter"
                    className={styles.socialNetworkIcon}
                  />
                </a>
              </span>
            )}
            {data.youtubeUrl && (
              <span role="listitem">
                <a href={data.youtubeUrl}>
                  <img
                    src={youtubeLogo}
                    alt="youtube"
                    className={styles.socialNetworkIcon}
                  />
                </a>
              </span>
            )}
            {data.linkedinUrl && (
              <span role="listitem">
                <a href={data.linkedinUrl}>
                  <img
                    src={linkedInLogo}
                    alt="linkedIn"
                    className={styles.socialNetworkIcon}
                  />
                </a>
              </span>
            )}
          </div>
        </div>
        <div
          className={isOpen ? styles.arrowBoxOpen : styles.arrowBox}
          onClick={() => setIsOpen(!isOpen)}
          onKeyPress={handleKeypress}
          aria-expanded={isOpen}
          role="button"
          tabIndex={0}
        >
          <img src={arrow} className={styles.arrowIcon} alt="Pied de page" />
        </div>
      </div>
      <div
        className={
          isOpen ? styles.collapsibleContentOpen : styles.collapsibleContent
        }
      >
        <div
          className={`${styles.organizingAuthorityLogoBox} ${styles.hideOnMobile}`}
        >
          {data.organizingAuthorityTitle && (
            <div className={styles.organizingAuthorityTitle}>
              {data.organizingAuthorityTitle}
            </div>
          )}
          {data.organizingAuthorityLogo && (
            <img
              src={data.organizingAuthorityLogo.url}
              alt={data.organizingAuthorityLogo.alt}
              className={styles.organizingAuthorityLogo}
            />
          )}
        </div>
        <FooterTableMenu data={menuData} />
        <FooterBarMenu data={menuData} />
        <div
          className={`${styles.organizingAuthorityLogoBox} ${styles.showOnMobile}`}
        >
          {data.organizingAuthorityTitle && (
            <div className={styles.organizingAuthorityTitle}>
              {data.organizingAuthorityTitle}
            </div>
          )}
          {data.organizingAuthorityLogo && (
            <img
              src={data.organizingAuthorityLogo.url}
              alt={data.organizingAuthorityLogo.alt}
              className={styles.organizingAuthorityLogo}
            />
          )}
        </div>
      </div>
    </footer>
  )
}

export const footerFragment = graphql`
  fragment FooterFields on DatoCmsFooter {
    facebookUrl
    twitterUrl
    linkedinUrl
    youtubeUrl
    transportMethodsLink {
      id
      name
      pictogram {
        alt
        url
        format
      }
    }
    organizingAuthorityTitle
    organizingAuthorityLogo {
      alt
      url
    }
    subsidiaryLogo {
      alt
      url
      format
      width
      height
    }
  }

  fragment FooterMenuFields on DatoCmsMenu {
    label
    footerDisplay1
    page {
      slug
    }
    column1 {
      label
      footerDisplay
      page {
        slug
      }
    }
    label2
    footerDisplay2
    page2 {
      slug
    }
    column2 {
      label
      footerDisplay
      page {
        slug
      }
    }
    label3
    footerDisplay3
    page3 {
      slug
    }
    column3 {
      label
      footerDisplay
      page {
        slug
      }
    }
    label4
    footerDisplay4
    page4 {
      slug
    }
    column4 {
      label
      footerDisplay
      page {
        slug
      }
    }
    label5
    footerDisplay5
    page5 {
      slug
    }
    column5 {
      label
      footerDisplay
      page {
        slug
      }
    }
    barMenu {
      label
      page {
        slug
      }
    }
  }
`
export default Footer
